import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LogOut, LayoutDashboard, Users, Wallet, Banknote, Settings, Building2, Receipt, Briefcase, FileBarChart, CreditCard, UserCog, ShieldCheck, BookOpen, TrendingUp } from "lucide-react";

const NAV = {
  GLOBAL_ADMIN: [
    { to: "/admin", icon: LayoutDashboard, label: "Overview" },
    { to: "/admin/companies", icon: Building2, label: "Tenants" },
    { to: "/admin/plans", icon: Briefcase, label: "Plans" },
    { to: "/admin/payments", icon: Receipt, label: "Payments" },
    { to: "/admin/reports", icon: FileBarChart, label: "Reports" },
    { to: "/admin/settings", icon: Settings, label: "Platform Settings" },
  ],
  COMPANY_ADMIN: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/customers", icon: Users, label: "Customers" },
    { to: "/savings", icon: Wallet, label: "Savings" },
    { to: "/loans", icon: Banknote, label: "Loans" },
    { to: "/finance", icon: TrendingUp, label: "Finance" },
    { to: "/accounting", icon: BookOpen, label: "Accounting" },
    { to: "/staff", icon: UserCog, label: "Staff" },
    { to: "/reports", icon: FileBarChart, label: "Reports" },
    { to: "/billing", icon: CreditCard, label: "Billing" },
  ],
  USER: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/customers", icon: Users, label: "Customers" },
    { to: "/savings", icon: Wallet, label: "Savings" },
    { to: "/loans", icon: Banknote, label: "Loans" },
    { to: "/finance", icon: TrendingUp, label: "Finance" },
    { to: "/accounting", icon: BookOpen, label: "Accounting" },
  ],
  CUSTOMER: [
    { to: "/portal", icon: LayoutDashboard, label: "My Portal" },
    { to: "/portal/loans", icon: Banknote, label: "My Loans" },
    { to: "/portal/savings", icon: Wallet, label: "My Savings" },
  ],
};

export default function Sidebar({ platformName = "KREPP" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  if (!user) return null;
  const items = NAV[user.role] || [];

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <aside className="w-64 h-screen sticky top-0 bg-background border-r border-border flex flex-col" data-testid="app-sidebar">
      <div className="px-6 py-6 border-b border-border">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <div className="w-9 h-9 rounded-md bg-forest grid place-items-center text-white">
            <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-none tracking-tight">{platformName}</div>
            <div className="label mt-1">Financial Platform</div>
          </div>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/admin" || it.to === "/portal"}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive ? "bg-forest text-white" : "text-foreground hover:bg-muted"
              }`
            }
            data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <it.icon className="w-4 h-4" strokeWidth={1.5} />
            <span>{it.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-border">
        <Link to="/profile" className="block px-3 py-2 mb-2 hover:bg-muted rounded-md transition-colors" data-testid="profile-link">
          <div className="text-sm font-medium truncate" data-testid="user-name">{user.name}</div>
          <div className="text-xs text-muted-foreground truncate" data-testid="user-email">{user.email}</div>
          <div className="label mt-1" data-testid="user-role">{user.role.replace("_", " ")}</div>
        </Link>
        <button onClick={handleLogout} className="btn-ghost w-full" data-testid="logout-btn">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </aside>
  );
}
